import { useState, useCallback, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { useRetailerId, usePartnerId } from '../../../../utils/routing/params.hooks'
import { useCurrentAccountDetails } from '../../../../api/current-account/currentAccount.hooks'
import { useRetailerSatisfactionSurveyMutation } from '../../../../__codegen__/api'
import { useIppToolName } from '../../../../routes/utils/useTotemInfo'
import { ToolSurveyModal } from './ToolSurveyModal'
import {
  canShowSurvey,
  recordSurveySubmitted,
  recordSurveyDismissed,
} from './surveyFrequencyTracker'
import { useToolSurveyAccess } from './useToolSurveyAccess'

const SUCCESS_DISPLAY_MS = 10000

export interface UseToolSurveyOptions {
  /** Workflow slug that triggered the survey (e.g., 'save-collection') */
  workflowSlug: string
  /** Bypass frequency checks (for testing) */
  alwaysShow?: boolean
}

export interface UseToolSurveyResult {
  /** Call this to trigger the survey (e.g., after a successful save) */
  triggerSurvey: () => void
  /** The survey modal element - MUST be rendered in your component's JSX (renders via portal to document.body) */
  SurveyModal: React.ReactNode
}

/**
 * Hook for triggering tool surveys from user actions
 *
 * This hook handles everything internally:
 * - Roulette/feature toggle check
 * - Frequency control
 * - Tool name derivation from Totem
 * - GraphQL submission
 * - Modal rendering (via portal to document.body)
 *
 * Usage:
 * ```tsx
 * const { triggerSurvey, SurveyModal } = useToolSurvey({
 *   workflowSlug: 'save-collection',
 * })
 *
 * const handleSave = async () => {
 *   await saveData()
 *   triggerSurvey()
 * }
 *
 * return (
 *   <>
 *     <Button onClick={handleSave}>Save</Button>
 *     {SurveyModal}
 *   </>
 * )
 * ```
 *
 * Or with useUserInitiatedSearchSuccess:
 * ```tsx
 * const { triggerSurvey, SurveyModal } = useToolSurvey({ workflowSlug: 'find-customer-orders' })
 * useUserInitiatedSearchSuccess({ filterParams, searchResponseType, onSuccess: triggerSurvey })
 * // Remember to render {SurveyModal} somewhere in your JSX
 * ```
 */
export const useToolSurvey = ({
  workflowSlug,
  alwaysShow = false,
}: UseToolSurveyOptions): UseToolSurveyResult => {
  const [isTriggered, setIsTriggered] = useState(false)
  const [isOpen, setIsOpen] = useState(false)
  const [showSuccess, setShowSuccess] = useState(false)
  const [shownAt, setShownAt] = useState<string | null>(null)

  // Check if the tool survey feature is enabled via roulette
  const isToolSurveyEnabled = useToolSurveyAccess()

  const { ippToolName, externalId } = useIppToolName()
  const retailerId = useRetailerId({ throwOnMissing: false }) ?? '0'
  const partnerId = usePartnerId({ throwOnMissing: false }) ?? '0'
  const { account } = useCurrentAccountDetails()

  const toolDisplayName = ippToolName
  const milestoneSlug = externalId ?? 'unknown-tool'

  const [submitSurvey, { loading: isSubmitting }] = useRetailerSatisfactionSurveyMutation()

  // Handle trigger and frequency check
  useEffect(() => {
    const checkAndShowSurvey = async () => {
      // Early exit if roulette is not enabled
      if (!isToolSurveyEnabled && !alwaysShow) {
        if (isTriggered) {
          setIsTriggered(false)
        }
        return
      }

      if (isTriggered && !isOpen && !showSuccess) {
        const result = await canShowSurvey(milestoneSlug, workflowSlug, { alwaysShow })

        if (result.canShow) {
          setIsOpen(true)
          setShownAt(new Date().toISOString())
        }
        // Reset trigger regardless of whether we showed the survey
        setIsTriggered(false)
      }
    }

    checkAndShowSurvey()
  }, [
    isTriggered,
    isOpen,
    showSuccess,
    milestoneSlug,
    workflowSlug,
    alwaysShow,
    isToolSurveyEnabled,
  ])

  const triggerSurvey = useCallback(() => {
    setIsTriggered(true)
  }, [])

  const handleClose = useCallback(async () => {
    if (isOpen && !showSuccess) {
      await recordSurveyDismissed(milestoneSlug, workflowSlug)
    }
    setIsOpen(false)
    setShowSuccess(false)
    setShownAt(null)
  }, [isOpen, showSuccess, milestoneSlug, workflowSlug])

  const handleSubmit = useCallback(
    async (rating: number, feedback: string) => {
      const submittedAt = new Date().toISOString()

      const surveyResponses = [{ surveyQuestion: 'toolRating', surveyResponse: String(rating) }]

      if (feedback.trim()) {
        surveyResponses.push({
          surveyQuestion: 'additionalFeedback',
          surveyResponse: feedback.trim(),
        })
      }

      try {
        await submitSurvey({
          variables: {
            retailerId,
            accountId: account?.id ?? '',
            partnerId,
            urlPath: window.location.pathname,
            surveyResponses,
            shownAt: shownAt ?? submittedAt,
            submittedAt,
            milestoneSlug,
            workflowSlug,
          },
        })

        await recordSurveySubmitted(milestoneSlug, workflowSlug)
        setShowSuccess(true)
      } catch (error) {
        await recordSurveySubmitted(milestoneSlug, workflowSlug)
        console.error('[useToolSurvey] Failed to submit survey:', error)
        handleClose()
      }
    },
    [
      submitSurvey,
      retailerId,
      account?.id,
      partnerId,
      shownAt,
      milestoneSlug,
      workflowSlug,
      handleClose,
    ]
  )

  // Create the modal element - renders via portal to document.body
  const SurveyModal = useMemo(() => {
    const modalElement = (
      <ToolSurveyModal
        toolName={toolDisplayName}
        isOpen={isOpen}
        onClose={handleClose}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        showSuccess={showSuccess}
        autoCloseAfterSuccessMs={SUCCESS_DISPLAY_MS}
      />
    )

    // Use portal to render at document.body level
    if (typeof document !== 'undefined') {
      return createPortal(modalElement, document.body)
    }

    return modalElement
  }, [toolDisplayName, isOpen, handleClose, handleSubmit, isSubmitting, showSuccess])

  return {
    triggerSurvey,
    SurveyModal,
  }
}
